import { pluralize } from 'utils/misc'
import { fromApiSchedule } from 'features/schedulingEngine/adapters/fromApiSchedule'
import {
  AVAILABILITY_MODE,
  AVAILABILITY_MODE_LABELS,
  END_RULE_TYPE,
  END_RULES_COMBINE,
  REPEAT_MODE,
} from 'features/schedulingEngine/constants'
import { buildScheduleLabel } from 'features/schedulingEngine/summaries/plainLanguage'
import { summarizeAnchorShort, summarizeCombinedShort } from 'features/schedulingEngine/summaries/anchorSummary'
import { summarizeRepeat } from 'features/schedulingEngine/summaries/repeatSummary'
import { namesForAnchor } from 'features/schedulingEngine/summaries/entityNames'
import { shouldShowDeployTimeOfDay } from 'features/schedulingEngine/summaries/deployTimeOfDayDisplay'
import { formatRelativeToVisitEndRule } from 'features/schedulingEngine/summaries/endRuleSummary'
import { formatDurationLabel, DURATION_UNIT } from 'features/schedulingEngine/constants'
import { formatTimeOfDay } from './studyTimelineTimeWindow'
import { SCHEDULE, TIME } from './constants'

const toUiSchedule = apiEntry => fromApiSchedule(apiEntry)

const buildEntityOptions = (visitIdToName = {}, instrumentIdToName = {}) => ({
  visits: Object.entries(visitIdToName).map(([id, name]) => ({ id, name })),
  instruments: Object.entries(instrumentIdToName).map(([id, name]) => ({ id, name })),
})

export const getEngineScheduleStartTypeLabel = schedule => buildScheduleLabel(toUiSchedule(schedule))

export const getEngineScheduleStartLabel = (schedule, visitIdToName = {}, instrumentIdToName = {}) => {
  const ui = toUiSchedule(schedule)
  const entityOptions = buildEntityOptions(visitIdToName, instrumentIdToName)
  const nameFor = anchor => namesForAnchor(anchor, entityOptions)
  let headline = ui.combineAnchors
    ? summarizeCombinedShort(ui, nameFor)
    : summarizeAnchorShort(ui.anchors?.[0], nameFor(ui.anchors?.[0]))
  if (shouldShowDeployTimeOfDay(ui)) {
    const timeLabel = formatTimeOfDay(ui.timeOfDay || schedule?.deploy?.time_of_day)
    if (timeLabel && headline) headline = `${headline} (${timeLabel})`
    else if (timeLabel) headline = timeLabel
  }
  return headline || ''
}

const summarizeEngineEndRules = (schedule, ui) => {
  const rules = ui?.endRules?.rules || schedule?.end_rules?.rules || []
  if (!rules.length) return null
  const bits = rules
    .map(rule => {
      const type = rule?.type
      if (type === END_RULE_TYPE.afterNOccurrences || type === 'after_n_occurrences') {
        const count = rule.count ?? rule.recurrences
        if (count != null) return `${SCHEDULE.AFTER_OCCURRENCES} ${count} ${SCHEDULE.OCCURRENCES}`
      }
      if (type === END_RULE_TYPE.relativeToVisit || type === 'relative_to_visit') {
        return formatRelativeToVisitEndRule(rule)
      }
      return null
    })
    .filter(Boolean)
  if (!bits.length) return null
  const combine = ui?.endRules?.combine || schedule?.end_rules?.combine
  if (bits.length === 1) return bits[0]
  const joiner =
    combine === END_RULES_COMBINE.latestOf || combine === 'latest_of'
      ? 'latest of'
      : combine === END_RULES_COMBINE.earliestOf || combine === 'earliest_of'
        ? 'earliest of'
        : ''
  return joiner ? `${joiner} ${bits.join(' or ')}` : bits.join(' or ')
}

export const getEngineScheduleExpireLabel = schedule => {
  const ui = toUiSchedule(schedule)
  const fromRules = summarizeEngineEndRules(schedule, ui)
  if (fromRules) return fromRules
  if (schedule?.availability) {
    const availability = ui.availability || {}
    if (availability.mode === AVAILABILITY_MODE.fixedDuration && availability.openFor?.value != null) {
      const unit = availability.openFor?.unit || DURATION_UNIT.hours
      const duration = formatDurationLabel(availability.openFor.value, unit)
      return `${duration} after start`
    }
  }
  return TIME.NEVER
}

const capitalize = text => {
  if (!text) return text
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const getEngineScheduleRepeatLabel = schedule => {
  const ui = toUiSchedule(schedule)
  const mode = ui.repeat?.mode
  if (!mode || mode === REPEAT_MODE.doesNotRepeat || mode === 'does_not_repeat') {
    return TIME.NO_REPEAT
  }
  if (mode === REPEAT_MODE.everyNDays || mode === 'every_n_days') {
    const interval = ui.repeat.intervalDays ?? schedule?.repeat?.interval_days ?? 1
    return `${SCHEDULE.EVERY} ${pluralize(Number(interval) || 1, 'day', 'days')}`
  }
  if (mode === 'persistent' || schedule?.recurring?.scheme === 'persistent') {
    return 'Persistent (daily)'
  }
  const summary = summarizeRepeat(ui.repeat)
  return summary ? capitalize(summary) : TIME.NO_REPEAT
}

export const getEngineScheduleVisibleLabel = schedule => {
  const ui = toUiSchedule(schedule)
  const availability = ui.availability || {}
  const daily = availability.dailyWindow || schedule?.availability?.daily_window
  if (daily?.enabled && daily.from && daily.until) {
    return `${TIME.AT_SPECIFIC_TIMES} (${daily.from}–${daily.until})`
  }
  if (availability.mode === AVAILABILITY_MODE.fixedDuration) {
    const value = availability.openFor?.value
    const unit = availability.openFor?.unit || 'hours'
    if (value != null) return `Open for ${value} ${unit}`
  }
  if (availability.mode && availability.mode !== AVAILABILITY_MODE.fixedDuration) {
    return AVAILABILITY_MODE_LABELS[availability.mode] || TIME.ALL_DAY_VISIBILITY
  }
  return TIME.ALL_DAY_VISIBILITY
}
